import React, { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import DataTable from "react-data-table-component";
import { InputField } from "../../../../Components/formElements/InputField";
import { useSelector } from "react-redux";
import { TextAreaField } from "../../../../Components/formElements/TextAreaField";

import RowConfirmModal from "./RowConfirmModal";

const tableHeadStyle = {
  headRow: { style: { backgroundColor: "#ebf0f5", fontWeight: "bold" } },
};

const getConfirmStatus = (row) => {
  const hasSE = !!row.sellerName?.trim();

  if (!hasSE) {
    // No SE assigned — Finance confirms directly
    if (!row.financeConfirmation?.confirmed) return "pending_finance";
    return "approved";
  }

  // Normal flow — SE first, then Finance
  if (!row.seConfirmation?.confirmed) return "pending_se";
  if (!row.financeConfirmation?.confirmed) return "pending_finance";
  return "approved";
};

const STATUS_BADGE = {
  pending_se: <span className="badge bg-warning text-dark">Pending SE</span>,
  pending_finance: (
    <span className="badge bg-info text-dark">Pending Finance</span>
  ),
  approved: <span className="badge bg-success">✓ Approved</span>,
};

const PAYMENT_MODES = [
  "Cash",
  "Cheque",
  "Card",
  "Online",
  "UPI",
  "Paytm",
  "Card&Cash",
  "Cheque&Cash",
  "NR",
];

const fmtDate = (date) => {
  if (!date) return "";
  const [year, month, day] = date.split("-");
  return day && month && year ? `${day}-${month}-${year}` : date;
};

// ── Reusable cell components ──────────────────────────────────────────────────
const ReadCell = ({ value }) => (
  <div className="m-1">
    <InputField className="inputBox" value={value ?? ""} disabled />
  </div>
);

const EditCell = ({ value, type = "text", onChange }) => (
  <div className="m-1">
    <InputField
      className="inputBox"
      type={type}
      value={value ?? ""}
      onChange={onChange}
    />
  </div>
);

const ReadTextAreaCell = ({ value }) => (
  <div className="m-1">
    <TextAreaField className="inputBox" value={value ?? ""} disabled />
  </div>
);

const EditTextAreaCell = ({ value, type = "text", onChange }) => (
  <div className="m-1">
    <TextAreaField
      className="inputBox"
      type={type}
      value={value ?? ""}
      onChange={onChange}
    />
  </div>
);

const SelectCell = ({ value, options, onChange }) => (
  <select
    className="form-select form-select-sm inputBox"
    value={value ?? ""}
    onChange={onChange}
  >
    <option value="">Select</option>
    {options.map((opt) => (
      <option key={opt} value={opt}>
        {opt}
      </option>
    ))}
  </select>
);

// Service Executive cell — dropdown for dispatch manager, read-only for others
const SellerSelectCell = ({ value, options, onChange, disabled }) => {
  if (disabled) {
    return (
      <div className="m-2">
        <InputField className="inputBox" value={value ?? ""} disabled />
      </div>
    );
  }
  return (
    <select
      className="form-select form-select-sm inputBox"
      value={value ?? ""}
      onChange={onChange}
    >
      <option value="">Select Executive</option>
      {options.map((opt) => (
        <option key={opt.id || opt.code} value={opt.name}>
          {opt.name}
        </option>
      ))}
    </select>
  );
};

const ConfirmDetailCell = ({ confirmation }) => {
  if (!confirmation?.confirmed) return <span className="text-muted">—</span>;

  const dt = new Date(confirmation.confirmedAt);
  const date = dt.toLocaleDateString("en-GB");
  const time = dt.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });

  return (
    <div style={{ lineHeight: "1.6" }}>
      <div>{date}</div>
      <div className="text-muted">{time}</div>
      {confirmation.remarks && (
        <div className="text-muted">{confirmation.remarks}</div>
      )}
    </div>
  );
};

// ── Row factory functions ─────────────────────────────────────────────────────

const makeNewBillingRow = (
  index,
  prevBillingBalance = 0,
  defaultSeller = "",
  defaultSalePoint = "",
  defaultCostCenter = "",
) => ({
  local_id: `pay-${index}`,
  paymentDate: new Date().toISOString().split("T")[0],
  salePoint: defaultSalePoint,
  receivedLocation: defaultSalePoint,
  costCenter: defaultCostCenter,
  billingTotalAmt: String(prevBillingBalance),
  paymentMode: "",
  billingReceivedAmt: "",
  chequeNo: "NA",
  cardNo: "NA",
  bank: "NA",
  billingBalanceAmt: String(prevBillingBalance),
  sellerName: defaultSeller,
  paymentStatus: "",
});

const makeNewSecurityRow = (
  index,
  prevSecurityBalance = 0,
  defaultSeller = "",
  defaultSalePoint = "",
  defaultCostCenter = "",
) => ({
  local_id: `sec-${index}`,
  paymentDate: new Date().toISOString().split("T")[0],
  salePoint: defaultSalePoint,
  receivedLocation: defaultSalePoint,
  costCenter: defaultCostCenter,
  securityTotalAmt: String(prevSecurityBalance),
  paymentMode: "",
  securityReceivedAmt: "",
  chequeNo: "NA",
  cardNo: "NA",
  bank: "NA",
  securityBalanceAmt: String(prevSecurityBalance),
  sellerName: defaultSeller,
  paymentStatus: "",
});

const makeNewCreditNoteRow = (index) => ({
  local_id: `cn-${index}`,
  creditNoteDate: new Date().toISOString().split("T")[0],
  creditNoteBy: "",
  amountType: "",
  status: "",
  amount: "",
  returnBy: "",
  remarks: "",
  refundPaid: "",
});

// =============================================================================
const ERFExtensionPaymentAdjustmentDetails = ({
  showSecurity = true, // Extensions may have security
  paymentHistory = [],
  securityHistory = [],
  creditNoteHistory = [],
  onDataChange,
  serviceExecutiveList = [],

  allowAddBilling = true,
  allowAddSecurity = true,
  allowAddCreditNote = true,

  isServiceExecutive = false,
  isFinanceExecutive = false,
  onConfirmRow,

  defaultSalePoint = "",
  defaultCostCenter = "",
  isAdminOrPCD = false,
}) => {
  const [isVisible, setIsVisible] = useState(true);

  let userData = useSelector((state) => state?.user);

  const [canAddPayments, setCanAddPayments] = useState();
  const [isDispatchManager, setIsDispatchManager] = useState(false);
  const [currentUserName, setCurrentUserName] = useState("");

  const [confirmModal, setConfirmModal] = useState(null);

  useEffect(() => {
    if (userData?.data?.roles) {
      const allowedRoles = ["SERVICE-EXECUTIVE", "DISPATCH", "SALES-ADMIN"];
      const isAllowed = userData?.data?.roles.some((r) =>
        allowedRoles.includes(r.roleId),
      );
      setCanAddPayments(isAllowed);

      // dispatch manager check
      const isDispatch = userData?.data?.roles.some(
        (r) => r.roleId === "DISPATCH",
      );
      setIsDispatchManager(isDispatch);

      setCurrentUserName(
        userData?.data?.firstName ||
          userData?.data?.name ||
          userData?.data?.userId ||
          "",
      );
    }
  }, [userData]);

  // Reset Rows
  useEffect(() => {
    setNewBillingRows([]);
    setNewSecurityRows([]);
    setNewCreditNoteRows([]);
  }, [paymentHistory, securityHistory, creditNoteHistory]);

  // New rows added by the user this session — these have local_id → editable
  const [newBillingRows, setNewBillingRows] = useState([]);
  const [newSecurityRows, setNewSecurityRows] = useState([]);
  const [newCreditNoteRows, setNewCreditNoteRows] = useState([]);

  // Combined: API rows (read-only) on top + new rows (editable) below
  const allBillingRows = [...paymentHistory, ...newBillingRows];
  const allSecurityRows = showSecurity
    ? [...securityHistory, ...newSecurityRows]
    : [];
  const allCreditNoteRows = [...creditNoteHistory, ...newCreditNoteRows];

  // ── Notify parent with the latest merged arrays ───────────────────────────
  // Numeric fields are parsed to numbers here before sending to parent/backend.
  // Internal state stays as strings because HTML inputs need string values to work.

  const BILLING_NUMERIC_FIELDS = [
    "billingTotalAmt",
    "billingReceivedAmt",
    "billingBalanceAmt",
  ];
  const SECURITY_NUMERIC_FIELDS = [
    "securityTotalAmt",
    "securityReceivedAmt",
    "securityBalanceAmt",
  ];
  const CREDIT_NUMERIC_FIELDS = ["amount", "refundPaid"];

  const parseNumericFields = (rows, numericFields) =>
    rows.map((row) => {
      const parsed = { ...row };
      numericFields.forEach((field) => {
        if (parsed[field] !== undefined && parsed[field] !== "") {
          parsed[field] = parseFloat(parsed[field]) || 0;
        }
      });
      return parsed;
    });

  const notify = (billing, security, creditNote) => {
    if (!onDataChange) return;
    onDataChange({
      paymentHistory: parseNumericFields(
        [...paymentHistory, ...billing],
        BILLING_NUMERIC_FIELDS,
      ),
      securityHistory: parseNumericFields(
        [...securityHistory, ...security],
        SECURITY_NUMERIC_FIELDS,
      ),
      creditNoteHistory: parseNumericFields(
        [...creditNoteHistory, ...creditNote],
        CREDIT_NUMERIC_FIELDS,
      ),
    });
  };

  // ── LIVE SUMMARY CALCULATIONS ─────────────────────────────────────────────

  const A =
    allBillingRows.reduce(
      (sum, row) => sum + (parseFloat(row.billingReceivedAmt) || 0),
      0,
    ) +
    allSecurityRows.reduce(
      (sum, row) => sum + (parseFloat(row.securityReceivedAmt) || 0),
      0,
    );

  const B = allCreditNoteRows.reduce(
    (sum, row) => sum + (parseFloat(row.amount) || 0),
    0,
  );

  const lastBillingBalance =
    parseFloat(allBillingRows.at(-1)?.billingBalanceAmt ?? 0) || 0;

  const lastSecurityBalance = showSecurity
    ? parseFloat(allSecurityRows.at(-1)?.securityBalanceAmt ?? 0) || 0
    : 0;

  const C = lastBillingBalance + lastSecurityBalance;

  // // const balAmt = Math.max(0, parseFloat((C - B).toFixed(2)));
  // // const returnedAmount = Math.max(0, parseFloat((B - C).toFixed(2)));

  // const netReceived = parseFloat((A - B).toFixed(2));
  // const balAmt = Math.max(0, parseFloat((netReceived - C).toFixed(2)));
  // const returnedAmount = Math.max(0, parseFloat((B - A + C).toFixed(2)));

  const B_creditNote = allCreditNoteRows
    .filter((row) => row.status !== "Refund")
    .reduce((sum, row) => sum + (parseFloat(row.amount) || 0), 0);

  const B_refund = allCreditNoteRows
    .filter((row) => row.status === "Refund")
    .reduce((sum, row) => sum + (parseFloat(row.amount) || 0), 0);

  const effectiveBalance = parseFloat((C - B).toFixed(2));
  const balAmt = Math.max(0, effectiveBalance);
  const netReceived = parseFloat((A - B_refund).toFixed(2));

  // ── CHANGE HANDLERS ───────────────────────────────────────────────────────

  const handleBillingChange = (local_id, field, value) => {
    setNewBillingRows((prev) => {
      const updated = prev.map((row) => {
        if (row.local_id !== local_id) return row;
        const u = { ...row, [field]: value };
        u.billingBalanceAmt = (
          (parseFloat(u.billingTotalAmt) || 0) -
          (parseFloat(u.billingReceivedAmt) || 0)
        ).toFixed(2);
        return u;
      });
      notify(updated, newSecurityRows, newCreditNoteRows);
      return updated;
    });
  };

  const handleSecurityChange = (local_id, field, value) => {
    setNewSecurityRows((prev) => {
      const updated = prev.map((row) => {
        if (row.local_id !== local_id) return row;
        const u = { ...row, [field]: value };
        u.securityBalanceAmt = (
          (parseFloat(u.securityTotalAmt) || 0) -
          (parseFloat(u.securityReceivedAmt) || 0)
        ).toFixed(2);
        return u;
      });
      notify(newBillingRows, updated, newCreditNoteRows);
      return updated;
    });
  };

  const handleCreditNoteChange = (local_id, field, value) => {
    setNewCreditNoteRows((prev) => {
      const updated = prev.map((row) =>
        row.local_id === local_id ? { ...row, [field]: value } : row,
      );
      notify(newBillingRows, newSecurityRows, updated);
      return updated;
    });
  };

  // ── ADD ROW ───────────────────────────────────────────────────────────────

  const handleAddBillingRow = () => {
    const index = paymentHistory.length + newBillingRows.length + 1;
    const prevBalance =
      parseFloat(allBillingRows.at(-1)?.billingBalanceAmt ?? 0) || 0;
    const newRow = makeNewBillingRow(
      index,
      prevBalance,
      currentUserName,
      defaultSalePoint,
      defaultCostCenter,
    );
    setNewBillingRows((prev) => {
      const updated = [...prev, newRow];
      notify(updated, newSecurityRows, newCreditNoteRows);
      return updated;
    });
  };

  const handleAddSecurityRow = () => {
    const index = securityHistory.length + newSecurityRows.length + 1;
    const prevBalance =
      parseFloat(allSecurityRows.at(-1)?.securityBalanceAmt ?? 0) || 0;
    const newRow = makeNewSecurityRow(
      index,
      prevBalance,
      currentUserName,
      defaultSalePoint,
      defaultCostCenter,
    );
    setNewSecurityRows((prev) => {
      const updated = [...prev, newRow];
      notify(newBillingRows, updated, newCreditNoteRows);
      return updated;
    });
  };

  const handleAddCreditNoteRow = () => {
    const index = creditNoteHistory.length + newCreditNoteRows.length + 1;
    const newRow = makeNewCreditNoteRow(index);
    setNewCreditNoteRows((prev) => {
      const updated = [...prev, newRow];
      notify(newBillingRows, newSecurityRows, updated);
      return updated;
    });
  };

  // ── REMOVE ROW ────────────────────────────────────────────────────────────

  const handleRemoveBillingRow = (local_id) => {
    setNewBillingRows((prev) => {
      const updated = prev.filter((row) => row.local_id !== local_id);
      notify(updated, newSecurityRows, newCreditNoteRows);
      return updated;
    });
  };

  const handleRemoveSecurityRow = (local_id) => {
    setNewSecurityRows((prev) => {
      const updated = prev.filter((row) => row.local_id !== local_id);
      notify(newBillingRows, updated, newCreditNoteRows);
      return updated;
    });
  };

  const handleRemoveCreditNoteRow = (local_id) => {
    setNewCreditNoteRows((prev) => {
      const updated = prev.filter((row) => row.local_id !== local_id);
      notify(newBillingRows, newSecurityRows, updated);
      return updated;
    });
  };

  const ConfirmCell = (row, rowIndex, tableKey) => {
    if (row.local_id) {
      return <p className="text-muted">Save first</p>;
    }

    const hasSE = !!row.sellerName?.trim();
    const status = getConfirmStatus(row);

    // SE can only act on rows assigned to an SE and in pending_se state
    const canSEAct = isServiceExecutive && hasSE && status === "pending_se";
    const canFinanceAct = isFinanceExecutive && status === "pending_finance";

    return (
      <div style={{ padding: "4px 0" }}>
        {STATUS_BADGE[status]}

        {/* Show a small hint on no-SE rows so Finance knows why SE step is skipped */}
        {!hasSE && !row.financeConfirmation?.confirmed && (
          <p className="mb-0 mt-1" style={{ color: "#888" }}>
            No SE
          </p>
        )}

        {row.seConfirmation?.confirmed && (
          <p className="mb-0 mt-1" style={{ color: "#555" }}>
            SE: {row.seConfirmation.confirmedBy}
          </p>
        )}
        {row.financeConfirmation?.confirmed && (
          <p className="mb-0" style={{ color: "#555" }}>
            Fin: {row.financeConfirmation.confirmedBy}
          </p>
        )}

        {canSEAct && (
          <button
            className="btn btn-sm btn-outline-warning mt-1"
            onClick={() =>
              setConfirmModal({ tableKey, rowIndex, role: "serviceExecutive" })
            }
          >
            Confirm
          </button>
        )}

        {canFinanceAct && (
          <button
            className="btn btn-sm btn-outline-success mt-1"
            onClick={() =>
              setConfirmModal({ tableKey, rowIndex, role: "financeExecutive" })
            }
          >
            Approve
          </button>
        )}
      </div>
    );
  };

  const handleConfirmSubmit = ({ remarks }) => {
    const { tableKey, rowIndex, role } = confirmModal;
    setConfirmModal(null);

    if (onConfirmRow) {
      onConfirmRow(tableKey, rowIndex, role, { remarks });
    }
  };

  // ── BILLING COLUMNS ───────────────────────────────────────────────────────

  const billingColumns = [
    {
      name: "Payment Date",
      width: "180px",
      cell: (row) =>
        row.local_id ? (
          <EditCell
            type="date"
            value={row.paymentDate}
            onChange={(e) =>
              handleBillingChange(row.local_id, "paymentDate", e.target.value)
            }
          />
        ) : (
          <ReadCell value={fmtDate(row.paymentDate)} />
        ),
    },
    {
      name: "Sale Point",
      width: "190px",
      cell: (row) =>
        row.local_id ? (
          <EditCell
            value={row.salePoint}
            onChange={(e) =>
              handleBillingChange(row.local_id, "salePoint", e.target.value)
            }
          />
        ) : (
          <ReadCell value={row.salePoint} />
        ),
    },
    {
      name: "Received Location",
      width: "180px",
      cell: (row) =>
        row.local_id ? (
          <EditCell
            value={row.receivedLocation}
            onChange={(e) =>
              handleBillingChange(
                row.local_id,
                "receivedLocation",
                e.target.value,
              )
            }
          />
        ) : (
          <ReadCell value={row.receivedLocation} />
        ),
    },
    {
      name: "Total Bill Amt.",
      width: "155px",
      cell: (row) => <ReadCell value={row.billingTotalAmt} />,
    },
    {
      name: "Payment Mode",
      width: "150px",
      cell: (row) =>
        row.local_id ? (
          <SelectCell
            value={row.paymentMode}
            options={PAYMENT_MODES}
            onChange={(e) =>
              handleBillingChange(row.local_id, "paymentMode", e.target.value)
            }
          />
        ) : (
          <ReadCell value={row.paymentMode} />
        ),
    },
    {
      name: "Received Amt",
      width: "180px",
      cell: (row) =>
        row.local_id ? (
          <EditCell
            type="number"
            value={row.billingReceivedAmt}
            onChange={(e) =>
              handleBillingChange(
                row.local_id,
                "billingReceivedAmt",
                e.target.value,
              )
            }
          />
        ) : (
          <ReadCell value={row.billingReceivedAmt} />
        ),
    },
    {
      name: "Cheque No.",
      width: "200px",
      cell: (row) =>
        row.local_id ? (
          <EditCell
            value={row.chequeNo}
            onChange={(e) =>
              handleBillingChange(row.local_id, "chequeNo", e.target.value)
            }
          />
        ) : (
          <ReadCell value={row.chequeNo ?? "NA"} />
        ),
    },
    {
      name: "Card No.",
      width: "200px",
      cell: (row) =>
        row.local_id ? (
          <EditCell
            value={row.cardNo}
            onChange={(e) =>
              handleBillingChange(row.local_id, "cardNo", e.target.value)
            }
          />
        ) : (
          <ReadCell value={row.cardNo ?? "NA"} />
        ),
    },
    {
      name: "Bank",
      width: "180px",
      cell: (row) =>
        row.local_id ? (
          <EditCell
            value={row.bank}
            onChange={(e) =>
              handleBillingChange(row.local_id, "bank", e.target.value)
            }
          />
        ) : (
          <ReadCell value={row.bank ?? "NA"} />
        ),
    },
    {
      name: "Balance Amt.",
      width: "180px",
      cell: (row) => <ReadCell value={row.billingBalanceAmt} />,
    },
    {
      name: "Service Executive",
      width: "190px",
      cell: (row) =>
        row.local_id ? (
          <SellerSelectCell
            value={row.sellerName}
            options={serviceExecutiveList}
            disabled={!isDispatchManager} // ← only dispatch manager can change
            onChange={(e) =>
              handleBillingChange(row.local_id, "sellerName", e.target.value)
            }
          />
        ) : (
          <ReadCell value={row.sellerName} />
        ),
    },

    {
      name: "Status",
      width: "145px",
      cell: (row, rowIndex) => ConfirmCell(row, rowIndex, "billing"),
    },
    {
      name: "SE Confirmation",
      width: "200px",
      cell: (row) => <ConfirmDetailCell confirmation={row.seConfirmation} />,
    },
    {
      name: "Finance Confirmation",
      width: "200px",
      cell: (row) => (
        <ConfirmDetailCell confirmation={row.financeConfirmation} />
      ),
    },
    {
      name: "Action",
      width: "80px",
      cell: (row) =>
        row.local_id ? (
          <button
            className="btn btn-danger btn-sm mt-1"
            onClick={() => handleRemoveBillingRow(row.local_id)}
          >
            <i className="bi bi-trash3" />
          </button>
        ) : null,
    },
  ];

  // ── SECURITY COLUMNS ──────────────────────────────────────────────────────

  const securityColumns = [
    {
      name: "Payment Date",
      width: "180px",
      cell: (row) =>
        row.local_id ? (
          <EditCell
            type="date"
            value={row.paymentDate}
            onChange={(e) =>
              handleSecurityChange(row.local_id, "paymentDate", e.target.value)
            }
          />
        ) : (
          <ReadCell value={fmtDate(row.paymentDate)} />
        ),
    },
    {
      name: "Sale Point",
      width: "190px",
      cell: (row) =>
        row.local_id ? (
          <EditCell
            value={row.salePoint}
            onChange={(e) =>
              handleSecurityChange(row.local_id, "salePoint", e.target.value)
            }
          />
        ) : (
          <ReadCell value={row.salePoint} />
        ),
    },
    {
      name: "Received Location",
      width: "180px",
      cell: (row) =>
        row.local_id ? (
          <EditCell
            value={row.receivedLocation}
            onChange={(e) =>
              handleSecurityChange(
                row.local_id,
                "receivedLocation",
                e.target.value,
              )
            }
          />
        ) : (
          <ReadCell value={row.receivedLocation} />
        ),
    },
    {
      name: "Total Security Amt.",
      width: "155px",
      cell: (row) => <ReadCell value={row.securityTotalAmt} />,
    },
    {
      name: "Payment Mode",
      width: "150px",
      cell: (row) =>
        row.local_id ? (
          <SelectCell
            value={row.paymentMode}
            options={PAYMENT_MODES}
            onChange={(e) =>
              handleSecurityChange(row.local_id, "paymentMode", e.target.value)
            }
          />
        ) : (
          <ReadCell value={row.paymentMode} />
        ),
    },
    {
      name: "Received Amt",
      width: "180px",
      cell: (row) =>
        row.local_id ? (
          <EditCell
            type="number"
            value={row.securityReceivedAmt}
            onChange={(e) =>
              handleSecurityChange(
                row.local_id,
                "securityReceivedAmt",
                e.target.value,
              )
            }
          />
        ) : (
          <ReadCell value={row.securityReceivedAmt} />
        ),
    },
    {
      name: "Cheque No.",
      width: "200px",
      cell: (row) =>
        row.local_id ? (
          <EditCell
            value={row.chequeNo}
            onChange={(e) =>
              handleSecurityChange(row.local_id, "chequeNo", e.target.value)
            }
          />
        ) : (
          <ReadCell value={row.chequeNo ?? "NA"} />
        ),
    },
    {
      name: "Card No.",
      width: "200px",
      cell: (row) =>
        row.local_id ? (
          <EditCell
            value={row.cardNo}
            onChange={(e) =>
              handleSecurityChange(row.local_id, "cardNo", e.target.value)
            }
          />
        ) : (
          <ReadCell value={row.cardNo ?? "NA"} />
        ),
    },
    {
      name: "Bank",
      width: "180px",
      cell: (row) =>
        row.local_id ? (
          <EditCell
            value={row.bank}
            onChange={(e) =>
              handleSecurityChange(row.local_id, "bank", e.target.value)
            }
          />
        ) : (
          <ReadCell value={row.bank ?? "NA"} />
        ),
    },
    {
      name: "Balance Amt.",
      width: "180px",
      cell: (row) => <ReadCell value={row.securityBalanceAmt} />,
    },
    {
      name: "Service Executive",
      width: "190px",
      cell: (row) =>
        row.local_id ? (
          <SellerSelectCell
            value={row.sellerName}
            options={serviceExecutiveList}
            disabled={!isDispatchManager}
            onChange={(e) =>
              handleSecurityChange(row.local_id, "sellerName", e.target.value)
            }
          />
        ) : (
          <ReadCell value={row.sellerName} />
        ),
    },
    {
      name: "Status",
      width: "145px",
      cell: (row, rowIndex) => ConfirmCell(row, rowIndex, "security"),
    },
    {
      name: "SE Confirmation",
      width: "200px",
      cell: (row) => <ConfirmDetailCell confirmation={row.seConfirmation} />,
    },
    {
      name: "Finance Confirmation",
      width: "200px",
      cell: (row) => (
        <ConfirmDetailCell confirmation={row.financeConfirmation} />
      ),
    },
    {
      name: "Action",
      width: "80px",
      cell: (row) =>
        row.local_id ? (
          <button
            className="btn btn-danger btn-sm mt-1"
            onClick={() => handleRemoveSecurityRow(row.local_id)}
          >
            <i className="bi bi-trash3" />
          </button>
        ) : null,
    },
  ];

  // ── CREDIT NOTE COLUMNS ───────────────────────────────────────────────────

  const creditNoteColumns = [
    {
      name: "Credit Note Date",
      width: "180px",
      cell: (row) =>
        row.local_id ? (
          <EditCell
            type="date"
            value={row.creditNoteDate}
            onChange={(e) =>
              handleCreditNoteChange(
                row.local_id,
                "creditNoteDate",
                e.target.value,
              )
            }
          />
        ) : (
          <ReadCell value={fmtDate(row.creditNoteDate)} />
        ),
    },
    {
      name: "Credit Note By",
      width: "190px",
      cell: (row) =>
        row.local_id ? (
          <EditCell
            value={row.creditNoteBy}
            onChange={(e) =>
              handleCreditNoteChange(
                row.local_id,
                "creditNoteBy",
                e.target.value,
              )
            }
          />
        ) : (
          <ReadCell value={row.creditNoteBy} />
        ),
    },
    {
      name: "Amount Type",
      width: "180px",
      cell: (row) =>
        row.local_id ? (
          <SelectCell
            value={row.amountType}
            options={["Billing", "Security"]}
            onChange={(e) =>
              handleCreditNoteChange(row.local_id, "amountType", e.target.value)
            }
          />
        ) : (
          <ReadCell value={row.amountType} />
        ),
    },
    {
      name: "Status",
      width: "155px",
      cell: (row) =>
        row.local_id ? (
          <SelectCell
            value={row.status}
            options={["Credit Note", "Refund", "Adjusted"]}
            onChange={(e) =>
              handleCreditNoteChange(row.local_id, "status", e.target.value)
            }
          />
        ) : (
          <ReadCell value={row.status} />
        ),
    },
    {
      name: "Amount",
      width: "150px",
      cell: (row) =>
        row.local_id ? (
          <EditCell
            type="number"
            value={row.amount}
            onChange={(e) =>
              handleCreditNoteChange(row.local_id, "amount", e.target.value)
            }
          />
        ) : (
          <ReadCell value={row.amount} />
        ),
    },
    {
      name: "Return By",
      width: "150px",
      cell: (row) =>
        row.local_id ? (
          <SelectCell
            value={row.returnBy}
            options={["Cash", "Cheque", "Online", "Other"]}
            onChange={(e) =>
              handleCreditNoteChange(row.local_id, "returnBy", e.target.value)
            }
          />
        ) : (
          <ReadCell value={row.returnBy} />
        ),
    },
    {
      name: "Remarks",
      width: "250px",
      cell: (row) =>
        row.local_id ? (
          <EditTextAreaCell
            value={row.remarks}
            onChange={(e) =>
              handleCreditNoteChange(row.local_id, "remarks", e.target.value)
            }
          />
        ) : (
          <ReadTextAreaCell value={row.remarks} />
        ),
    },
    {
      name: "Refund Paid",
      width: "180px",
      cell: (row) =>
        row.local_id ? (
          <EditCell
            type="number"
            value={row.refundPaid}
            onChange={(e) =>
              handleCreditNoteChange(row.local_id, "refundPaid", e.target.value)
            }
          />
        ) : (
          <ReadCell value={row.refundPaid} />
        ),
    },
    {
      name: "Status",
      width: "145px",
      cell: (row, rowIndex) => ConfirmCell(row, rowIndex, "creditNote"),
    },
    {
      name: "Finance Confirmation",
      width: "200px",
      cell: (row) => (
        <ConfirmDetailCell confirmation={row.financeConfirmation} />
      ),
    },
    {
      name: "Action",
      width: "80px",
      cell: (row) =>
        row.local_id ? (
          <button
            className="btn btn-danger btn-sm mt-1"
            onClick={() => handleRemoveCreditNoteRow(row.local_id)}
          >
            <i className="bi bi-trash3" />
          </button>
        ) : null,
    },
  ];

  // ── RENDER ────────────────────────────────────────────────────────────────
  return (
    <div className="accordion mb-4" id="accordionPaymentAdjusted">
      <div className="accordion-item">
        <h2 className="accordion-header">
          <Row className="accordion-button">
            <Col xs={1} className="text-left arrowBtn">
              <i
                className={`bi ${isVisible ? "bi-chevron-down" : "bi-chevron-right"}`}
                onClick={() => setIsVisible((v) => !v)}
              />
            </Col>
            <Col xs={1} className="text-center logoBtn">
              <span className="iconLogo">P</span>
            </Col>
            <Col xs={10} md={8}>
              <span className="headerTitle">Payment Adjusted Details</span>
            </Col>
          </Row>
        </h2>

        <div
          className={`accordion-collapse collapse show ${isVisible ? "visible" : ""}`}
        >
          <div className="accordion-body">
            {/* ── SUMMARY BAR ────────────────────────────────────────────── */}
            {/* <Row className="mb-3 pt-2 border-bottom pb-3">
              <Col
                xs={12}
                lg={3}
                className="commTopButtonRightLeftPadding mb-1"
              >
                <span className="fw-medium">
                  (A) Total Received:{" "}
                  <strong style={{ color: A > 0 ? "green" : "inherit" }}>
                    {A.toFixed(2)}
                  </strong>
                </span>
              </Col>
              <Col
                xs={12}
                lg={3}
                className="commTopButtonRightLeftPadding mb-1"
              >
                <span className="fw-medium">
                  (B) Credit Note Deducted:{" "}
                  <strong style={{ color: B > 0 ? "red" : "inherit" }}>
                    {B.toFixed(2)}
                  </strong>
                </span>
              </Col>
              <Col
                xs={12}
                lg={3}
                className="commTopButtonRightLeftPadding mb-1"
              >
                <span className="fw-medium">
                  (A - B) Net Received:{" "}
                  <strong style={{ color: netReceived >= 0 ? "green" : "red" }}>
                    {netReceived.toFixed(2)}
                  </strong>
                </span>
              </Col>
              <Col
                xs={12}
                lg={3}
                className="commTopButtonRightLeftPadding mb-1"
              >
                <span className="fw-medium">
                  (C) Earlier Bal:{" "}
                  <strong style={{ color: C > 0 ? "red" : "inherit" }}>
                    {C.toFixed(2)}
                  </strong>
                </span>
              </Col>
              <Col
                xs={12}
                lg={4}
                className="commTopButtonRightLeftPadding mb-1"
              >
                <span className="fw-medium">
                  <strong>Final Bal: </strong>
                  <strong style={{ color: balAmt === 0 ? "green" : "red" }}>
                    {balAmt.toFixed(2)}
                  </strong>
                  {" / "}
                  <strong>Returned: </strong>
                  <strong
                    style={{ color: returnedAmount > 0 ? "red" : "green" }}
                  >
                    {returnedAmount.toFixed(2)}
                  </strong>
                </span>
              </Col>
            </Row> */}

            <Row className="mb-3 pt-2 border-bottom pb-3">
              {/* Row 1 — what was received and adjusted */}
              <Col
                xs={12}
                lg={3}
                className="commTopButtonRightLeftPadding mb-1"
              >
                <span className="fw-medium">
                  (A) Total Received:{" "}
                  <strong style={{ color: A > 0 ? "green" : "inherit" }}>
                    {A.toFixed(2)}
                  </strong>
                </span>
              </Col>
              <Col
                xs={12}
                lg={3}
                className="commTopButtonRightLeftPadding mb-1"
              >
                <span className="fw-medium">
                  (B1) Credit Note:{" "}
                  <strong
                    style={{ color: B_creditNote > 0 ? "orange" : "inherit" }}
                  >
                    {B_creditNote.toFixed(2)}
                  </strong>
                </span>
              </Col>
              <Col
                xs={12}
                lg={3}
                className="commTopButtonRightLeftPadding mb-1"
              >
                <span className="fw-medium">
                  (B2) Refund:{" "}
                  <strong
                    style={{ color: B_refund > 0 ? "purple" : "inherit" }}
                  >
                    {B_refund.toFixed(2)}
                  </strong>
                </span>
              </Col>

              {/* Row 2 — balance position */}
              <Col
                xs={12}
                lg={3}
                className="commTopButtonRightLeftPadding mb-1"
              >
                <span className="fw-medium">
                  (C) Balance After Payments:{" "}
                  <strong style={{ color: C > 0 ? "red" : "inherit" }}>
                    {C.toFixed(2)}
                  </strong>
                </span>
              </Col>
              <Col
                xs={12}
                lg={3}
                className="commTopButtonRightLeftPadding mb-1"
              >
                <span className="fw-medium">
                  <strong>Net Amount Due (C−B1−B2): </strong>
                  <strong style={{ color: balAmt === 0 ? "green" : "red" }}>
                    {balAmt.toFixed(2)}
                  </strong>
                </span>
              </Col>

              <Col xs={12} lg={3} className="commTopButtonRightLeftPadding mb-1">
              <span className="fw-medium">
                (A − B2) Net Received:{" "}
                <strong style={{ color: netReceived >= 0 ? "green" : "red" }}>
                  {netReceived.toFixed(2)}
                </strong>
              </span>
            </Col>
            </Row>

            {/* ── BILLING AMOUNT TABLE ──────────────────────────────────── */}
            <div className="mb-4">
              <Row className="bg-light p-2 border rounded-top align-items-center">
                <Col xs={8} md={10} className="commTopButtonRightLeftPadding">
                  <h6 className="fw-bold mb-0 text-primary ps-2">
                    Billing Amount
                  </h6>
                </Col>
                {canAddPayments && allowAddBilling && (
                  <Col xs={4} md={2} className="commTopButtonRightLeftPadding">
                    <button className="btnTable" onClick={handleAddBillingRow}>
                      <i className="bi bi-plus-lg" /> Billing Amount
                    </button>
                  </Col>
                )}
              </Row>
              <DataTable
                columns={billingColumns}
                data={allBillingRows}
                customStyles={tableHeadStyle}
                noDataComponent={
                  <div className="p-2 text-muted text-center">
                    No billing records found
                  </div>
                }
              />
            </div>

            {/* ── SECURITY AMOUNT TABLE ─────────────────────────────────── */}
            {showSecurity && (
              <div className="mb-4">
                <Row className="bg-light p-2 border rounded-top align-items-center">
                  <Col xs={8} md={10} className="commTopButtonRightLeftPadding">
                    <h6 className="fw-bold mb-0 text-primary ps-2">
                      Security Amount
                    </h6>
                  </Col>
                  {canAddPayments && allowAddSecurity && (
                    <Col
                      xs={4}
                      md={2}
                      className="commTopButtonRightLeftPadding"
                    >
                      <button
                        className="btnTable"
                        onClick={handleAddSecurityRow}
                      >
                        <i className="bi bi-plus-lg" /> Security Amt
                      </button>
                    </Col>
                  )}
                </Row>
                <DataTable
                  columns={securityColumns}
                  data={allSecurityRows}
                  customStyles={tableHeadStyle}
                  noDataComponent={
                    <div className="p-2 text-muted text-center">
                      No security records found
                    </div>
                  }
                />
              </div>
            )}

            {/* ── CREDIT NOTE AMOUNT TABLE ──────────────────────────────── */}
            <div className="mb-4">
              <Row className="bg-light p-2 border rounded-top align-items-center">
                <Col xs={8} md={10} className="commTopButtonRightLeftPadding">
                  {allCreditNoteRows.length > 0 && (
                    <h6 className="fw-bold mb-0 text-primary ps-2">
                      Credit Note Amount
                    </h6>
                  )}
                </Col>

                {(canAddPayments || isAdminOrPCD) && allowAddCreditNote && (
                  <Col xs={4} md={2} className="commTopButtonRightLeftPadding">
                    <button
                      className="btnTable"
                      onClick={handleAddCreditNoteRow}
                    >
                      <i className="bi bi-plus-lg" /> Credit Note
                    </button>
                  </Col>
                )}
              </Row>
              <DataTable
                columns={creditNoteColumns}
                data={allCreditNoteRows}
                customStyles={tableHeadStyle}
                noDataComponent={
                  <div className="p-2 text-muted text-center">
                    No credit note records found
                  </div>
                }
              />
            </div>
          </div>
        </div>
      </div>
      <RowConfirmModal
        show={!!confirmModal}
        onHide={() => setConfirmModal(null)}
        role={confirmModal?.role}
        onConfirm={handleConfirmSubmit}
      />
    </div>
  );
};

export default ERFExtensionPaymentAdjustmentDetails;
